import { Trans, useLingui } from "@lingui/react/macro";
import { useForm, FormProvider } from "react-hook-form";
import Form from "@/components/Form/Form";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import TextArea from "@/components/TextArea/TextArea";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import type { IAcceptedConvertedPatientNote } from "@/api/PatientNote";
import { acceptConvertedPatientNote } from "@/api/PatientNote";
import styles from "./ConvertedPatientNoteForm.module.scss";
import { NoteStatusChip } from "@/components/Chips/NoteStatusChip";
import type {
  IConvertedPatientNoteWithPatientId,
  IPatientNoteStatus,
} from "@medoma/models/patientNotes";
import {
  patientNoteStates,
  patientNoteStatusSchema,
} from "@medoma/models/patientNotes";
import { humanFormat } from "@medoma/models/date-and-time";

const ConvertedPatientNoteForm = ({
  patientNote,
  convertedPatientNoteState,
}: {
  patientNote: IConvertedPatientNoteWithPatientId;
  isLoadingConvertedNote: boolean;
  convertedPatientNoteState: (noteState: string) => void;
}) => {
  const { t } = useLingui();
  const methods = useForm<IConvertedPatientNoteWithPatientId>({
    defaultValues: {
      id: patientNote.id,
      convertedText: patientNote.convertedText,
      acceptedBy: patientNote.acceptedBy,
      status: patientNote.status,
    },
  });

  const [isCurrentlyEditingAlreadyAccepted, setIsCurrentlyEditing] =
    useState(false);

  const conversionAccepted =
    patientNote.status === patientNoteStatusSchema.enum.accepted;

  const {
    handleSubmit,
    register,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    reset({
      id: patientNote.id,
      convertedText: patientNote.convertedText,
      acceptedBy: patientNote.acceptedBy,
      status: patientNote.status,
    });
  }, [patientNote, reset]);

  const queryClient = useQueryClient();

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const acceptedInfoText =
    patientNote.acceptedBy && patientNote.conversionTimeStamp
      ? `${patientNote.acceptedBy}, ${humanFormat(new Date(patientNote.conversionTimeStamp))}`
      : undefined;

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      convertedPatientNote,
    }: {
      convertedPatientNote: IAcceptedConvertedPatientNote & {
        status: IPatientNoteStatus;
      };
    }) => acceptConvertedPatientNote(convertedPatientNote),
    onError: () => {
      displayErrorMessageAlert(
        t`Det gick inte att godkänna och visa den översatta texten för patient. Försök igen om en stund, och rapportera ett fel om det ändå inte fungerar.`,
      );
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: () => {
      convertedPatientNoteState(patientNoteStates.SUBMITTED);
      return queryClient.invalidateQueries({ queryKey: ["conversion"] });
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const { id, convertedText, status } = formData;

    const convertedPatientNote = {
      id,
      updatedText: convertedText,
      status,
    };

    setIsCurrentlyEditing(false);

    mutate({ convertedPatientNote });
  });

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const currentConvertedText = watch().convertedText;

  const editConvertedPatientNote = () => {
    setIsCurrentlyEditing(true);
  };

  const cancelEditingConvertedPatientNote = () => {
    setIsCurrentlyEditing(false);
  };

  const splitConvertedTextInParagraphs = patientNote.convertedText
    .split("\n")
    .filter((paragraph) => paragraph.trim() !== "");

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <Heading level="h2">
          <Trans>Förenklad text</Trans>
        </Heading>
        <NoteStatusChip status={patientNote.status}>
          {acceptedInfoText}
        </NoteStatusChip>
        {!isCurrentlyEditingAlreadyAccepted && conversionAccepted ? (
          <>
            {splitConvertedTextInParagraphs.map((paragraph, index) => (
              <Text element="p" key={index}>
                {paragraph}
              </Text>
            ))}
          </>
        ) : (
          <Form.Section>
            <TextArea
              label={t`Ändra förenklad text`}
              showOptionalLabel={false}
              rows={10}
              errorMessage={errors.convertedText?.message}
              {...register("convertedText", {
                required: {
                  value: currentConvertedText?.length === 0,
                  message: t`Det finns ingen förenklad text att spara`,
                },
              })}
            />
          </Form.Section>
        )}
        <div className={styles.buttons}>
          {isCurrentlyEditingAlreadyAccepted ? (
            <>
              <FilledButton type="submit" disabled={isPending}>
                <Trans>Godkänn och visa för patient</Trans>
              </FilledButton>
              {conversionAccepted && (
                <OutlinedButton onClick={cancelEditingConvertedPatientNote}>
                  <Trans>Avbryt</Trans>
                </OutlinedButton>
              )}
            </>
          ) : (
            <>
              {conversionAccepted ? (
                <OutlinedButton onClick={editConvertedPatientNote}>
                  <Trans>Ändra förenklad text</Trans>
                </OutlinedButton>
              ) : (
                <FilledButton type="submit" disabled={isPending}>
                  <Trans>Godkänn och visa för patient</Trans>
                </FilledButton>
              )}
            </>
          )}
        </div>
      </Form>
    </FormProvider>
  );
};

export default ConvertedPatientNoteForm;
